import React from 'react'
import { Box, Button, Typography, makeStyles, Theme } from '@material-ui/core'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.25),
        marginBottom: theme.spacing(1),
        borderRadius: 8,
        border: `1px solid ${theme.palette.warning.light}`,
        backgroundColor: '#FFF8E1',
    },
    text: {
        fontSize: '0.8125rem',
        color: theme.palette.text.primary,
        lineHeight: 1.4,
        flex: 1,
        minWidth: 200,
    },
}))

function otherConfigLabel(count: number): string {
    return count === 1
        ? '1 other leasing season config'
        : `${count} other leasing season configs`
}

export const EditScopeBanner: React.FC<{
    artifactLabel: string
    usedByOtherConfigs: number
    /** True when an active season uses this artifact (in-place edit 409s). */
    lockedByActiveSeason?: boolean
    copying?: boolean
    onCreateCopy: () => void
}> = ({
    artifactLabel,
    usedByOtherConfigs,
    lockedByActiveSeason = false,
    copying = false,
    onCreateCopy,
}) => {
    const classes = useStyles()

    if (usedByOtherConfigs <= 0 && !lockedByActiveSeason) return null

    const others = otherConfigLabel(usedByOtherConfigs)
    const message =
        lockedByActiveSeason && usedByOtherConfigs <= 0
            ? `This ${artifactLabel} is locked because an active season uses it. Create a copy to change it here — that season keeps the original.`
            : lockedByActiveSeason
            ? `This ${artifactLabel} is used by ${others}. That config is on an active season, so this ${artifactLabel} can’t be edited in place. Create a copy to change it here — the other config keeps the original.`
            : `This ${artifactLabel} is used by ${others}. Saving here would change that config too. Create a copy to edit only this one.`

    return (
        <Box className={classes.root}>
            <Typography className={classes.text}>{message}</Typography>
            <Button
                size="small"
                color="primary"
                variant="contained"
                startIcon={<FileCopyOutlinedIcon />}
                disabled={copying}
                onClick={onCreateCopy}
            >
                Create a copy
            </Button>
        </Box>
    )
}
